import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { useClaimStatesCapability } from '@client/hooks/business/claim/useClaimStatesCapability';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Dropdown } from 'antd';
import { BrainCircuit, ChevronDown, DollarSign, FileText, Grid3x3, Milestone } from 'lucide-react';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/product-types')({
    staticData: {
        metadata: {
            title: 'Product types and PDS configuration',
        },
        ui: {
            icon: Grid3x3,
            title: 'Product types and PDS configuration',
            subtitle: 'Configure product types and PDS documents',
            menuDescription: 'Product types and PDS documents used to assess claims',
        },
        presentation: 'fullPage',
        modules: ['claim'],
        permissions: ['settings:update'],
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ProductTypeSearch />
        </PageController>
    );
}

type QueryParams = RouterInputs['productType']['listProductTypes'];

const columnHelper = createColumnHelper<
    RouterOutputs['productType']['listProductTypes']['rows'][number],
    QueryParams['orderBy']
>();

function ProductTypeSearch() {
    const orgId = useOrgId();
    const { mutate: deleteProductType } = trpc.productType.deleteProductType.useMutation();
    const navigate = useNavigate();
    const enableAiDecisionEngine = useFeatureFlag('enable-ai-decision-engine');
    const { isEnabled: enableClaimStates } = useClaimStatesCapability();

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        orderBy: { id: 'desc' },
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
    });

    const { data, isLoading, error, isFetching, refetch } = trpc.productType.listProductTypes.useQuery(input);

    const tableProps = usePrismaTable({
        data: data?.rows,
        total: data?.total,
        loading: isLoading,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        emptyState: { noRecordsTitle: 'No product types yet' },
        columnVisibility: {
            enabled: true,
            persistKey: 'product-types-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: refetch,
        },
        columns: [
            columnHelper.accessor('id', {
                id: 'id',
                header: 'ID',
                sortable: 'id',
                width: 80,
                cell: ({ row }) => row.id.toString(),
            }),
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Product type name',
                sortable: 'name',
                cell: ({ row }) => <span className="font-medium">{row.name}</span>,
            }),
            columnHelper.actions({
                width: 200,
                cell: ({ row }) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Button.Edit.Link
                            to="/console/$orgId/configuration/claims-products/product-types/edit/$productTypeId"
                            params={{ orgId, productTypeId: row.id }}
                        />
                        <Dropdown
                            trigger={['click']}
                            menu={{
                                items: [
                                    {
                                        key: 'financials',
                                        label: 'Financials',
                                        icon: <DollarSign size={14} className="text-green-600" />,
                                        onClick: () =>
                                            navigate({
                                                to: '/console/$orgId/configuration/claims-products/product-types/financials/$id',
                                                params: { orgId, id: row.id },
                                            }),
                                    },
                                    {
                                        key: 'pds',
                                        label: 'PDS',
                                        icon: <FileText size={14} className="text-blue-600" />,
                                        onClick: () =>
                                            navigate({
                                                to: '/console/$orgId/configuration/claims-products/product-types/$productTypeId',
                                                params: { orgId, productTypeId: row.id },
                                            }),
                                    },
                                    ...(enableClaimStates
                                        ? [
                                              {
                                                  key: 'states',
                                                  label: 'States',
                                                  icon: <Milestone size={14} className="text-accent-500" />,
                                                  onClick: () =>
                                                      navigate({
                                                          to: '/console/$orgId/configuration/claims-products/product-types/states/$id',
                                                          params: { orgId, id: row.id },
                                                      }),
                                              },
                                          ]
                                        : []),
                                    ...(enableAiDecisionEngine
                                        ? [
                                              {
                                                  key: 'ai',
                                                  label: 'Decision Engine',
                                                  icon: <BrainCircuit size={14} className="text-violet-600" />,
                                                  onClick: () =>
                                                      navigate({
                                                          to: '/console/$orgId/configuration/claims-products/product-types/$productTypeId/decision-engine',
                                                          params: { orgId, productTypeId: row.id },
                                                      }),
                                              },
                                          ]
                                        : []),
                                ],
                            }}
                        >
                            <Button type="tertiary" icon={<ChevronDown />} iconPosition="end">
                                Configure
                            </Button>
                        </Dropdown>
                        <Button.Delete
                            onConfirm={(onSuccess) => {
                                message.loading('Deleting product type...');
                                deleteProductType(
                                    { id: row.id },
                                    {
                                        onSuccess() {
                                            message.success('Product type deleted successfully');
                                            void refetch();
                                            onSuccess();
                                        },
                                        onError(error) {
                                            errorMessage.show(error);
                                        },
                                    },
                                );
                            }}
                            confirmTitle="Delete product type?"
                            confirmDescription={`Are you sure you want to delete "${row.name}"?`}
                        />
                    </div>
                ),
            }),
        ],
    });

    if (error) {
        return <InlineQueryError error={error} onRetry={() => void refetch()} retrying={isFetching} />;
    }

    return (
        <>
            <Outlet />

            <TableToolbar
                actions={
                    <Button.Add.Link
                        variant="primary"
                        label="New product type"
                        to="/console/$orgId/configuration/claims-products/product-types/add"
                        params={{ orgId }}
                    />
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/claims-products/product-types/edit/$productTypeId',
                                params: { orgId, productTypeId: id },
                            },
                            e,
                        );
                    },
                })}
            />
        </>
    );
}
